import { useCallback } from 'react';
import { filterSearchTags } from '@/helpers/filter-search-tags';
import { isDefaultOptionArray } from '@/helpers/other';
import { AsyncSelect } from '@/ui/select/async/async-select';
import { sharedProps } from '@/ui/select/select-shared';
import { useTags } from '@/features/tags/use-tags.query';
import type { IOption } from '@/types';
import { SmallText } from '@/ui/typography/action-panel';
import { styled } from 'goober';

interface Props {
  id?: string;
  label?: string;
  placeholder?: string;
  defaultValue: IOption[] | null;
  onSetOptions?: (options: IOption[]) => void;
  onChange?: (options: IOption[]) => void;
}

export const TagsSelect = ({
  id,
  label,
  defaultValue,
  placeholder,
  onSetOptions,
  onChange,
}: Props) => {
  const { tags } = useTags();

  const setOptions = useCallback(
    async (search: string) => {
      const { doesTagMatchSearchPhrase } = filterSearchTags({
        searchPhrase: search,
      });

      const tagList =
        search.length === 0 ? tags : tags.filter(doesTagMatchSearchPhrase);

      const tagOptions = tagList.map(tag => ({
        value: tag.id,
        label: tag.name,
      }));

      onSetOptions?.(tagOptions);
      return tagOptions;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags],
  );

  return (
    <AsyncSelect
      id={id}
      dataTestId={id}
      label={label}
      defaultValue={defaultValue}
      isMulti
      isSearchable
      setOptions={setOptions}
      onChange={option => {
        if (!isDefaultOptionArray(option)) return;

        onChange?.(option);
      }}
      placeholder={<Placeholder>{placeholder}</Placeholder>}
      {...sharedProps}
    />
  );
};

const Placeholder = styled(SmallText)`
  color: ${({ theme }) => theme.colors.gray.c8};
`;
